import { useEffect, useMemo, useState } from "react";
import {
  type OutlookCalendarCategory,
  type OutlookCalendarConfiguration,
  type OutlookFolder,
  type OutlookMailConfiguration,
  type OutlookTasksConfiguration,
} from "./OutlookSourceSettings.types";
import { OutlookSourceSettingsApi } from "./OutlookSourceSettings.Api";
import {
  SourceTypeIds,
  toSourceTypeId,
} from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type {
  SourceSettingsModel,
} from "../source-settings/SourceSettingsApiAccess";
import type { OutlookSourceSettingsViewModel } from "./OutlookSourceSettings.ViewModel.types";

const NONE_OPTION = "__none";

const toCategoryOption = (category: OutlookCalendarCategory) => ({
  id: category.id,
  label: category.displayName || category.id,
  category,
});

const flattenFolders = (
  folders: OutlookFolder[],
  depth = 0,
): Array<{ id: string; label: string; folder: OutlookFolder }> => {
  const flattened: Array<{ id: string; label: string; folder: OutlookFolder }> = [];

  folders.forEach((folder) => {
    if (!folder.id) {
      return;
    }

    const baseName = folder.displayName || folder.name || folder.id;
    flattened.push({
      id: folder.id,
      label: `${"  ".repeat(depth)}${baseName}`,
      folder,
    });

    if (Array.isArray(folder.childFolders) && folder.childFolders.length > 0) {
      flattened.push(...flattenFolders(folder.childFolders, depth + 1));
    }
  });

  return flattened;
};

export const useOutlookSourceSettingsViewModel = (
  model: SourceSettingsModel,
): OutlookSourceSettingsViewModel => {
  const [folders, setFolders] = useState<OutlookFolder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const sourceTypeId = toSourceTypeId(model.editingConnection.sourceTypeId);
  const isOutlookMail = sourceTypeId === SourceTypeIds.OutlookMail;
  const isOutlookTasks = sourceTypeId === SourceTypeIds.OutlookTasks;
  const isOutlookCalendar = sourceTypeId === SourceTypeIds.OutlookCalendar;
  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<
        OutlookMailConfiguration &
          OutlookTasksConfiguration &
          OutlookCalendarConfiguration
      >(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  useEffect(() => {
    const loadFolders = async (): Promise<void> => {
      if (
        !model.boardId ||
        !model.editingConnection.id ||
        !model.editingConnection.isAuthorised
      ) {
        setFolders([]);
        return;
      }

      if (!isOutlookMail && !isOutlookTasks && !isOutlookCalendar) {
        setFolders([]);
        return;
      }

      if (!model.sourceSettingsApi) {
        setFolders([]);
        return;
      }

      if (!sourceTypeId) {
        setFolders([]);
        return;
      }

      const outlookApi =
        model.sourceSettingsApi.tryGetSourceApi<OutlookSourceSettingsApi>(
          sourceTypeId,
        );
      if (!outlookApi) {
        setFolders([]);
        return;
      }

      setIsLoading(true);
      setHasError(false);
      try {
        if (isOutlookCalendar) {
          const response = await outlookApi.getCalendarCategories(
            model.boardId,
            model.editingConnection.id,
          );

          setFolders(response.map((category) => ({
            id: category.id,
            displayName: category.displayName || category.id,
          })));
        } else {
          const response = isOutlookMail
            ? await outlookApi.getMailFolders(
                model.boardId,
                model.editingConnection.id,
              )
            : await outlookApi.getTaskFolders(
                model.boardId,
                model.editingConnection.id,
              );

          setFolders(response);
        }
      } catch {
        setFolders([]);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void loadFolders();
  }, [
    isOutlookMail,
    isOutlookTasks,
    isOutlookCalendar,
    model.boardId,
    model.editingConnection.id,
    model.editingConnection.isAuthorised,
    model.sourceSettingsApi,
    sourceTypeId,
  ]);

  const folderOptions = useMemo(() => flattenFolders(folders), [folders]);
  const categoryOptions = useMemo(
    () =>
      folders.map((folder) =>
        toCategoryOption({
          id: folder.id,
          displayName: folder.displayName || folder.name || folder.id,
        }),
      ),
    [folders],
  );

  const selectedFolderId = useMemo(() => {
    if (isOutlookTasks) {
      return configuration.folderForTaskCreation?.id ?? NONE_OPTION;
    }

    return configuration.folders?.[0]?.id ?? NONE_OPTION;
  }, [configuration, isOutlookTasks]);

  const selectedFolderIds = useMemo(() => {
    if (!isOutlookMail) {
      return selectedFolderId === NONE_OPTION ? [] : [selectedFolderId];
    }
    return (configuration.folders ?? [])
      .map((folder) => folder.id)
      .filter((id): id is string => Boolean(id));
  }, [configuration, isOutlookMail, selectedFolderId]);

  const selectedCategoryId = useMemo(
    () => configuration.category?.id ?? NONE_OPTION,
    [configuration.category],
  );

  return {
    isVisible:
      (isOutlookMail || isOutlookTasks || isOutlookCalendar) &&
      model.editingConnection.isAuthorised,
    isOutlookMail,
    isOutlookTasks,
    isOutlookCalendar,
    isLoading,
    hasError,
    folderOptions,
    categoryOptions,
    selectedFolderIds,
    selectedFolderId,
    selectedFolderName:
      selectedFolderId === NONE_OPTION
        ? ""
        : folderOptions.find((folder) => folder.id === selectedFolderId)
            ?.label || "",
    selectedCategoryId,
    selectedCategoryName:
      selectedCategoryId === NONE_OPTION
        ? ""
        : categoryOptions.find((category) => category.id === selectedCategoryId)
            ?.label || configuration.category?.displayName || "",
    noneOptionValue: NONE_OPTION,
    onSelectFolders: (optionValues: string[]) => {
      const selectedIds = optionValues.filter((value) => value !== NONE_OPTION);

      updateConnectionConfiguration<OutlookMailConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => {
          if (selectedIds.length === 0) {
            return { ...cfg, folders: undefined };
          }

          const selectedFolders = folderOptions
            .filter((folder) => selectedIds.includes(folder.id))
            .map((folder) => folder.folder);

          if (selectedFolders.length === 0) {
            return cfg;
          }

          return {
            ...cfg,
            folders: selectedFolders,
          };
        },
      );
    },
    onSelectFolder: (optionValue?: string) => {
      const selectedId = String(optionValue || NONE_OPTION);
      updateConnectionConfiguration<OutlookTasksConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => {
          if (selectedId === NONE_OPTION) {
            return { ...cfg, folders: undefined, folderForTaskCreation: undefined };
          }

          const selected = folderOptions.find((folder) => folder.id === selectedId)
            ?.folder;
          if (!selected) {
            return cfg;
          }

          if (isOutlookTasks) {
            return {
              ...cfg,
              folders: [selected],
              folderForTaskCreation: selected,
            };
          }

          return {
            ...cfg,
            folders: [selected],
          };
        },
      );
    },
    onSelectCategory: (optionValue?: string) => {
      const selectedId = String(optionValue || NONE_OPTION);
      updateConnectionConfiguration<OutlookCalendarConfiguration>(
        model.editingConnection,
        model.onUpdate,
        (cfg) => {
          if (selectedId === NONE_OPTION) {
            return { ...cfg, category: undefined };
          }

          const selected = categoryOptions.find(
            (category) => category.id === selectedId,
          )?.category;
          if (!selected) {
            return cfg;
          }

          return {
            ...cfg,
            category: selected,
          };
        },
      );
    },
  };
};
